import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { StudentProgress } from './types';
import { quizSections } from './data/questions';
import QuizLayout from './components/QuizLayout';
import EmailForm from './components/EmailForm';
import Results from './components/Results';
import EvaluationLayout from './components/EvaluationLayout';
import SummaryPage from './components/SummaryPage';
import Certificate from './components/Certificate';
import { initializeProgress, loadProgress, saveProgress } from './utils/storage';
import { generateCertificate } from './utils/certificate';

function App() {
  const [studentProgress, setStudentProgress] = useState<StudentProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSummary, setShowSummary] = useState(false);

  const migrateProgress = (progress: StudentProgress): StudentProgress => {
    console.log('🔄 Migrating progress for', quizSections.length, 'sections...');
    console.log('📊 Current progress has', progress.sections.length, 'sections');

    // Ensure progress has all current sections
    const updatedSections = quizSections.map((section, index) => {
      // Try to find existing section by ID first, then by index
      let existingSection = progress.sections.find(s => s.id === section.id);
      if (!existingSection && progress.sections[index]?.id === section.id) {
        existingSection = progress.sections[index];
      }

      if (existingSection) {
        console.log(`✅ Found existing section: ${section.id}`);
        return {
          ...existingSection,
          name: section.name, // Update name in case it changed
          answers: existingSection.answers || []
        };
      }

      // Create new section if missing
      console.log(`➕ Creating new section: ${section.id}`);
      return {
        id: section.id,
        name: section.name,
        completed: false,
        score: 0,
        attempts: 0,
        answers: [],
        passed: false,
      };
    });

    const migratedProgress = {
      ...progress,
      sections: updatedSections,
      // Reset current section if it's out of bounds
      currentSection: progress.currentSection >= updatedSections.length ? 0 : progress.currentSection
    };

    console.log('✅ Migration complete. New sections:', migratedProgress.sections.map(s => s.id));
    return migratedProgress;
  };

  useEffect(() => {
    const savedProgress = loadProgress();
    if (savedProgress) {
      const migratedProgress = migrateProgress(savedProgress);
      setStudentProgress(migratedProgress);
      saveProgress(migratedProgress);
    }
    setIsLoading(false);
  }, []);

  const handleEmailSubmit = (studentInfo: { firstName: string; lastName: string; email: string; studentId: string }) => {
    const progress = initializeProgress(studentInfo, quizSections);
    setStudentProgress(progress);
    saveProgress(progress);
  };

  const updateProgress = (updatedProgress: StudentProgress) => {
    setStudentProgress(updatedProgress);
    saveProgress(updatedProgress);
  };

  const handleQuizEvaluated = (evaluatedProgress: StudentProgress) => {
    // Calculate the overall score based on section performance
    const allSectionsPassed = evaluatedProgress.sections.every(section => section.passed);
    const overallScore = allSectionsPassed ? 1.0 :
      evaluatedProgress.sections.reduce((sum, section) => sum + section.score, 0) / evaluatedProgress.sections.length;

    const completedProgress = {
      ...evaluatedProgress,
      overallScore,
      completedAt: new Date()
    };
    setStudentProgress(completedProgress);
    saveProgress(completedProgress);
  };


  const handleRetakeSection = (sectionIndex: number, retakeType: 'full' | 'partial' = 'full') => {
    if (studentProgress) {
      const currentSection = quizSections[sectionIndex];
      const currentSectionProgress = studentProgress.sections[sectionIndex];

      let updatedAnswers: any[] = [];

      if (retakeType === 'partial') {
        // For partial retakes, preserve ALL answers (both correct and incorrect)
        // The UI will hide correctly answered questions, but preserve their content
        updatedAnswers = currentSectionProgress?.answers || [];
      } else {
        // Clear all answers for full retakes
        updatedAnswers = [];
      }

      const updatedProgress = {
        ...studentProgress,
        currentSection: sectionIndex,
        completedAt: undefined,
        sections: studentProgress.sections.map((section, index) =>
          index === sectionIndex
            ? { ...section, completed: false, passed: false, answers: updatedAnswers }
            : section
        )
      };
      updateProgress(updatedProgress);
    }
  };

  const handleDownloadCertificate = () => {
    if (studentProgress) {
      generateCertificate(studentProgress);
    }
  };

  const resetQuiz = () => {
    setStudentProgress(null);
    setShowSummary(false);
    localStorage.removeItem('quiz-progress');
  };

  const handleRetakeQuiz = () => {
    if (studentProgress) {
      const resetProgress = {
        ...studentProgress,
        currentSection: 0,
        completedAt: undefined,
        overallScore: 0,
        sections: studentProgress.sections.map(section => ({
          ...section,
          completed: false,
          passed: false,
          answers: [],
          score: 0,
          attempts: 0,
          completedAt: undefined
        }))
      };
      updateProgress(resetProgress);
      setShowSummary(false);
    }
  };

  const handleContinueQuiz = () => {
    if (studentProgress) {
      const updatedProgress = {
        ...studentProgress,
        completedAt: undefined
      };
      updateProgress(updatedProgress);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-safety-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Safety Quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          <Route
            path="/"
            element={
              !studentProgress ? (
                <EmailForm onSubmit={handleEmailSubmit} />
              ) : studentProgress.certificateViewed && studentProgress.completedAt && Math.round(studentProgress.overallScore * 100) === 100 ? (
                <Navigate to="/certificate" replace />
              ) : studentProgress.completedAt ? (
                <Navigate to="/results" replace />
              ) : (
                <Navigate to="/quiz" replace />
              )
            }
          />
          <Route
            path="/quiz"
            element={
              studentProgress && !studentProgress.completedAt ? (
                <QuizLayout
                  progress={studentProgress}
                  onProgressUpdate={updateProgress}
                  onQuizEvaluated={handleQuizEvaluated}
                  onReset={resetQuiz}
                />
              ) : studentProgress && studentProgress.completedAt ? (
                <Navigate to="/results" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/results"
            element={
              studentProgress && studentProgress.completedAt ? (
                showSummary ? (
                  <SummaryPage
                    progress={studentProgress}
                    onRetakeSection={handleRetakeSection}
                    onDownloadCertificate={handleDownloadCertificate}
                    onReset={resetQuiz}
                    onProgressUpdate={updateProgress}
                  />
                ) : (
                  <EvaluationLayout
                    progress={studentProgress}
                    onComplete={() => setShowSummary(true)}
                    onRetakeQuiz={handleRetakeQuiz}
                    onRetakeSection={handleRetakeSection}
                    onProgressUpdate={updateProgress}
                  />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/certificate"
            element={
              studentProgress && studentProgress.completedAt && Math.round(studentProgress.overallScore * 100) === 100 ? (
                <Certificate
                  progress={studentProgress}
                  onReset={resetQuiz}
                />
              ) : studentProgress && studentProgress.completedAt ? (
                <Navigate to="/results" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;